import { Box, Typography } from "@mui/material";
// The stage rail — "where in the run we are", nothing else. Pure function of
// phase/stage: no timer, no spinner, no percentage. The approved progress
// master adapts to each existing pipeline without inventing completed steps.
export interface StageRailStage {
  key: string;
  label: string;
  /** Action number, or null for uploaded/extracted/complete markers. */
  number?: number | null;
}

export type StageRailPhase = "idle" | "running" | "done";

export interface StageRailProps {
  stages: StageRailStage[];
  /** -1 idle, 0..stages.length-1 running index, stages.length done. */
  stage: number;
  phase: StageRailPhase;
  title: string;
  note: string;
  /**
   * Whether a `phase === "done"` run actually finished all stages, vs.
   * stopping early (e.g. a replayed run that lands on "done" right after
   * its last real stage without running the rest). Defaults to `true` —
   * callers that can distinguish "finished for real" from "stopped early"
   * (e.g. checking a stage-result map for the last stage's presence) should
   * pass that signal through here instead of forcing every stage to render
   * as complete just because phase is "done".
   */
  finished?: boolean;
  /**
   * Stage keys whose result is not real yet (Bill Process: supplier not in the Address
   * Book, no PO found). The first one renders as "needs action", never a tick, and no
   * later stage is lit, whatever `stage` says.
   */
  needsAction?: string[];
}

const CheckGlyph = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const StageRail: React.FC<StageRailProps> = ({
  stages,
  stage,
  phase,
  title,
  note,
  finished = true,
  needsAction,
}) => {
  const isDone = phase === "done" && finished;
  const blockedAt = needsAction?.length
    ? stages.findIndex((s) => needsAction.includes(s.key))
    : -1;

  return (
    <Box
      aria-label="Progress"
      sx={{
        bgcolor: "background.paper",
        border: "1px solid var(--an-border)",
        borderRadius: "var(--an-r-card)",
        p: 3,
        fontFamily: "var(--an-font-chrome)",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: "18px",
        }}
      >
        <Typography
          component="h2"
          sx={{
            m: 0,
            fontSize: 14,
            lineHeight: "20px",
            fontWeight: 400,
            color: "var(--an-muted-2)",
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{ fontSize: 12, color: "text.secondary", textAlign: "right" }}
        >
          {note}
        </Typography>
      </Box>
      <Box
        component="ol"
        sx={{
          m: 0,
          p: 0,
          listStyle: "none",
          display: "grid",
          gridTemplateColumns: `repeat(${stages.length}, minmax(72px,1fr))`,
          overflowX: "auto",
          pb: 0,
        }}
      >
        {stages.map((s, i) => {
          const blocked = blockedAt >= 0 && i >= blockedAt;
          const complete = !blocked && (isDone || stage > i);
          const attention = blockedAt === i;
          const active =
            !complete && !blocked && phase === "running" && stage === i;
          const visual = complete
            ? "complete"
            : attention
            ? "attention"
            : active
            ? "active"
            : "pending";
          const stateWord = complete
            ? "complete"
            : attention
            ? "needs action"
            : active
            ? "working"
            : "waiting";
          const leftLit =
            (blockedAt < 0 || i <= blockedAt) && (isDone || stage >= i);
          const rightLit = !blocked && (isDone || stage > i);
          return (
            <Box
              component="li"
              key={s.key}
              className={`rail__stage--${visual}`}
              aria-label={`${s.label}: ${stateWord}`}
              sx={{ minWidth: 0 }}
            >
              <Box
                aria-hidden="true"
                sx={{ display: "flex", alignItems: "center" }}
              >
                <Box
                  sx={{
                    flex: 1,
                    height: "1px",
                    bgcolor: leftLit ? "primary.main" : "divider",
                    visibility: i === 0 ? "hidden" : "visible",
                  }}
                />
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    flexShrink: 0,
                    borderRadius: "50%",
                    border: "1px solid",
                    borderColor:
                      complete || active || attention
                        ? "primary.main"
                        : "grey.300",
                    bgcolor: complete ? "primary.main" : "background.paper",
                    color: complete
                      ? "primary.contrastText"
                      : active || attention
                      ? "primary.main"
                      : "text.secondary",
                    fontSize: 10,
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  {complete ? (
                    <CheckGlyph />
                  ) : s.number === null ? null : (
                    s.number ?? i + 1
                  )}
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    height: "1px",
                    bgcolor: rightLit ? "primary.main" : "divider",
                    visibility: i === stages.length - 1 ? "hidden" : "visible",
                  }}
                />
              </Box>
              <Typography
                sx={{
                  pt: 1.5,
                  px: 0.5,
                  fontSize: 13,
                  lineHeight: "22px",
                  textAlign: "center",
                  color:
                    active || attention ? "primary.main" : "text.secondary",
                }}
              >
                {s.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default StageRail;
